import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Button from "react-md/lib/Buttons/Button";
import { MSpinner } from "components/Shared";
import { getSketch } from "actions/homeowner/caseforms";
import SketchImageWrapper from "components/Homeowner/CaseForms/ReadOnly/SketchImageWrapper";
import PublicPropertyRecord from "components/Homeowner/CaseForms/PublicPropertyRecord";
import { WrappedMSplitTabView } from "components/Shared/MSplitTabView";
import { Sketch as WebApiSketch } from "interfaces/Mueller.Business.Models";

interface Props {
    // passed in
    controlNumber: number;  // Control number of the case    
    readOnly: boolean;      // Set to true to prevent sketch editing

    // From redux
    sketches: WebApiSketch[],
    token: string;
    fetchingSketches: boolean,
    getSketch: (controlNumber: number) => void,    
    completingCase: boolean;
    escalatingCase: boolean;
};

const Sketch = ({
    controlNumber,
    readOnly,
    sketches,
    token,
    fetchingSketches,
    getSketch,
    completingCase,
    escalatingCase,
} : Props) => {
    
    const [sketchVisible, setSketchVisible] = useState<boolean>(false);
    const [reloadImages, setReloadImages] = useState<boolean>(false);
    const sketchPopout = useRef<WindowProxy>(null);

    useEffect(() => {
        loadSketchData();
    }, []);

    useEffect(() => {
        if (sketchVisible && sketchPopout.current && !sketchPopout.current.closed && (completingCase || escalatingCase)) {
            sketchPopout.current.close();
            sketchPopout.current = null;
            setSketchVisible(false);
        }
    }, [completingCase, escalatingCase]);

    const loadSketchData = () => {
        setReloadImages(false);
        getSketch(controlNumber);
    };

    if (sketchVisible) {
        const timer = setInterval(() => {
            if (sketchPopout.current && sketchPopout.current.closed) {
                clearInterval(timer);
                loadSketchData();
                sketchPopout.current = null;
                setSketchVisible(false);
            }
        }, 1000);
    }

    const getSketchChild = (sketch) => {
        const sketchImages = sketch.sketchImages.map((item, ix) => {
            return <SketchImageWrapper key={ix} src={item} number={ix + 1} />;
        });            

        if (readOnly) {
            return (
                <div className="case-header">
                    {sketchImages}
                </div>
            );
        }
        const popout = () => window.open(`${sketch.uri}&SSO=${token}&DisplayMode=Standard`, "_blank", "alwaysRaised:true,height:768px,width:1024px,resizable:true");
        return (
            <div className="case-header">
                <PublicPropertyRecord controlNumber={controlNumber} />
                {
                    !sketchPopout.current &&
                    <Button
                        flat
                        primary
                        swapTheming
                        onClick={() => {
                            if (!sketchVisible && !sketchPopout.current) {
                                sketchPopout.current = popout();
                                setSketchVisible(true);
                            }
                        }}
                        style={{ marginBottom: "15px", marginTop: "15px" }}>
                        Open Sketch
                    </Button>
                }
                <br />
                <div>
                    {!reloadImages && sketchImages}
                </div>
            </div>
        );
    };

    if (fetchingSketches) {
        return <MSpinner />;
    }

    // the pop up window calls this function on the parent window, so it needs to be defined.
    // classic implementation uses this to reload the images, but we are doing that automatically when the window is closed.
    window["message"] = () => true; 

    const sections = sketches.map(s => ({
        id: s.sketchName,
        title: s.sketchName,
        children: getSketchChild(s),
        cmsTopicKey: s.cmsTopicKey,
    }));

    return (
        <WrappedMSplitTabView
            tabs={sections}
        />
    );
};

const mapStateToProps = state => ({
    fetchingSketches: state.caseForms.fetchingSketches,
    sketches: state.caseForms.sketches,
    token: state.user.profile.token,
    completingCase: state.qualityAssurance.qaVariables.completingCase,
    escalatingCase: state.qualityAssurance.qaVariables.escalatingCase,
});

const mapDispatchToProps = dispatch => ({
    getSketch(controlIdNumber) {
        dispatch(getSketch(controlIdNumber));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Sketch);

